
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "../styles/SpeakerManagementPage.css";
import DashboardLayout from "../components/DashboardLayout";

const SpeakerManagementPage = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const getStatus = (speaker) =>
        speaker.status || "Pending";
    const token = localStorage.getItem("token");

    const [speakersData, setSpeakersData] = useState([]);

    const speakerStats = {
        total: speakersData.length,

        approved: speakersData.filter(
            (speaker) =>
                getStatus(speaker).toLowerCase() === "approved"
        ).length,

        pending: speakersData.filter(
            (speaker) =>
                !["approved", "rejected"].includes(
                    getStatus(speaker).toLowerCase()
                )
        ).length,

        rejected: speakersData.filter(
            (speaker) =>
                getStatus(speaker).toLowerCase() === "rejected"
        ).length,
    };
    const [speakersLoading, setSpeakersLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const [speakerSearchInput, setSpeakerSearchInput] = useState("");
    const [speakerSearch, setSpeakerSearch] = useState("");

    const [speakerPage, setSpeakerPage] = useState(1);
    const [speakerPerPage] = useState(10);
    const [speakerTotalPages, setSpeakerTotalPages] = useState(1);
    const [event, setEvent] = useState(null);
    const [speakerSortColumn, setSpeakerSortColumn] =
        useState("created_at");
    const [speakerSortOrder, setSpeakerSortOrder] =
        useState("DESC");

    const [notification, setNotification] = useState(null);
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    const [speakerTalkDetails, setSpeakerTalkDetails] = useState(null);
    const [talkDetailsLoading, setTalkDetailsLoading] = useState(false);
    const [talkDetailsError, setTalkDetailsError] = useState("");
    const [detailsPanelWidth, setDetailsPanelWidth] = useState(420);
    // Notification helper
    const showNotification = (message, type = "success") => {
        setNotification({ message, type });

        window.setTimeout(() => {
            setNotification(null);
        }, 3000);
    };

    // Debounced speaker search
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSpeakerSearch(speakerSearchInput.trim());
            setSpeakerPage(1);
        }, 500);

        return () => window.clearTimeout(timer);
    }, [speakerSearchInput]);

    // Load speakers for the selected event
    const loadSpeakers = useCallback(
        async (signal) => {
            if (!eventId || !token) {
                setSpeakersData([]);
                setSpeakersLoading(false);
                return;
            }

            setSpeakersLoading(true);

            try {
                const params = new URLSearchParams({
                    page: String(speakerPage),
                    numPerPage: String(speakerPerPage),
                    sortByColumnName: speakerSortColumn,
                    sortBy: speakerSortOrder,
                    searchBy: speakerSearch,
                });

                const response = await fetch(
                    `${API_BASE_URL}/speaker_registration/${eventId}?${params.toString()}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        signal,
                    }
                );

                if (!response.ok) {
                    throw new Error(`Unable to load speakers (${response.status})`);
                }

                const data = await response.json();

                console.log("Speaker API response:", data);

                // Extract event details from the updated API response.
                setEvent(data);

                // Extract the speakers array.
                const speakerList = Array.isArray(data)
                    ? data
                    : Array.isArray(data.speakers)
                        ? data.speakers
                        : Array.isArray(data.result)
                            ? data.result
                            : [];
                console.log("Speaker records received:", speakerList.length);
                console.log("First speaker:", speakerList[0]);
                setSpeakersData(speakerList);

                setSpeakerTotalPages(speakerList.length < speakerPerPage
                    ? speakerPage
                    : speakerPage + 1
                );


                // Your existing API uses a second request to detect
                // whether another page exists.

            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Error loading speakers:", error);
                    showNotification(
                        error.message || "Failed to load speakers.",
                        "error"
                    );
                }
            } finally {
                if (!signal?.aborted) {
                    setSpeakersLoading(false);
                }
            }
        },
        [
            eventId,
            token,
            speakerPage,
            speakerPerPage,
            speakerSortColumn,
            speakerSortOrder,
            speakerSearch,
        ]
    );

    useEffect(() => {
        const controller = new AbortController();

        loadSpeakers(controller.signal);

        return () => controller.abort();
    }, [loadSpeakers]);

    // Sort table columns
    const handleSort = (column) => {
        setSpeakerPage(1);
        setSpeakerSortColumn(column);
        setSpeakerSortOrder((previous) =>
            speakerSortColumn === column
                ? previous === "ASC"
                    ? "DESC"
                    : "ASC"
                : "ASC"
        );
    };


    const openSpeakerTalkDetails = async (speaker) => {
        setSelectedSpeaker(speaker);
        setSpeakerTalkDetails(null);
        setTalkDetailsError("");
        setTalkDetailsLoading(true);

        try {
            // Use the speaker ID expected by your backend endpoint.
            const speakerId = speaker.id;

            if (!speakerId) {
                throw new Error("Speaker ID is missing.");
            }

            const response = await fetch(
                `${API_BASE_URL}/get_speaker_talkdetails/${speakerId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message || "Unable to load speaker talk details."
                );
            }

            setSpeakerTalkDetails(data);
        } catch (error) {
            console.error("Talk details error:", error);
            setTalkDetailsError(
                error.message || "Failed to load talk details."
            );
        } finally {
            setTalkDetailsLoading(false);
        }
    };

    const closeSpeakerTalkDetails = () => {
        setSelectedSpeaker(null);
        setSpeakerTalkDetails(null);
        setTalkDetailsError("");
    };


    // Accept or decline a speaker
    const handleSpeakerAction = async (registrationId, action) => {
        if (!token || !eventId) return;

        setActionLoading(registrationId);

        try {
            const response = await fetch(
                `${API_BASE_URL}/approve_speaker_registration`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        eventId,
                        registrationId,
                        action,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Action failed.");
            }

            const newStatus =
                action === "approve" ? "Approved" : "Rejected";

            setSpeakersData((previous) =>
                previous.map((speaker) =>
                    speaker.registration_id === registrationId
                        ? { ...speaker, status: newStatus }
                        : speaker
                )
            );

            showNotification(
                `Speaker ${newStatus.toLowerCase()} successfully.`
            );
        } catch (error) {
            console.error("Speaker action error:", error);
            showNotification(
                error.message || "Action failed. Please try again.",
                "error"
            );
        } finally {
            setActionLoading(null);
        }
    };

    // Download CSV using your existing backend endpoints
    const downloadFile = async (endpoint, fileName) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}${endpoint}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to download CSV.");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("CSV download error:", error);
            showNotification(error.message, "error");
        }
    };

    const downloadSpeakerCSV = () =>
        downloadFile(
            `/event_registratoin/send_speaker_report/${eventId}`,
            `event-${eventId}-speaker-report.csv`
        );

    const downloadSpeakerFormCSV = () =>
        downloadFile(
            `/download_speaker_registration_customform/${eventId}`,
            `event-${eventId}-speaker-form.csv`
        );

    const getSpeakerName = (speaker) =>
        speaker.name ||
        speaker.full_name ||
        [speaker.first_name, speaker.last_name]
            .filter(Boolean)
            .join(" ") ||
        "—";

    const getSpeakerEmail = (speaker) =>
        speaker.email_id ||
        speaker.email ||
        "—";

    const getTalkTopic = (speaker) =>
        speaker.talk_topic ||
        speaker.topic ||
        "—";

    // const getStatus = (speaker) =>
    //     speaker.status || "Pending";

    const formatDate = (value) => {
        if (!value) return "—";

        const date = new Date(value);

        return Number.isNaN(date.getTime())
            ? value
            : date.toLocaleDateString("en-CA");
    };

    if (!token) {
        return (
            <div className="speaker-page">
                <div className="speaker-page-empty">
                    Please log in to manage speakers.
                    <button
                        className="speaker-btn speaker-btn-primary"
                        onClick={() => navigate("/login")}
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <DashboardLayout currentSection="speaking">
            <div className="speaker-page">
                {notification && (
                    <div
                        className={`speaker-toast speaker-toast-${notification.type}`}
                        role="status"
                    >
                        {notification.message}
                    </div>
                )}

                {/* Page heading */}
                <div className="speaker-page-heading">
                    <div className="speaker-page-title-group">
                        <div className="speaker-page-icon">
                            <i className="fas fa-microphone-alt" />
                        </div>

                        <div>
                            <h1>Speaker Management</h1>
                            <p>
                                Manage speaker submissions, profiles and approvals.
                            </p>
                        </div>
                    </div>

                    <button
                        className="speaker-btn speaker-btn-outline"
                        onClick={() => navigate(-1)}
                    >
                        <i className="fas fa-arrow-left" />
                        Back to Events
                    </button>
                </div>

                {/* Speaker Statistics */}
                <div className="speaker-stats-grid">
                    <div className="speaker-stat-card">
                        <div className="speaker-stat-icon speaker-stat-total">
                            <i className="fas fa-user" />
                        </div>
                        <div>
                            <h3>{speakerStats.total}</h3>
                            <p>Total Speakers</p>
                        </div>
                    </div>

                    <div className="speaker-stat-card">
                        <div className="speaker-stat-icon speaker-stat-approved">
                            <i className="fas fa-check-circle" />
                        </div>
                        <div>
                            <h3>{speakerStats.approved}</h3>
                            <p>Approved</p>
                        </div>
                    </div>

                    <div className="speaker-stat-card">
                        <div className="speaker-stat-icon speaker-stat-pending">
                            <i className="fas fa-clock" />
                        </div>
                        <div>
                            <h3>{speakerStats.pending}</h3>
                            <p>Pending Review</p>
                        </div>
                    </div>

                    <div className="speaker-stat-card">
                        <div className="speaker-stat-icon speaker-stat-rejected">
                            <i className="fas fa-times-circle" />
                        </div>
                        <div>
                            <h3>{speakerStats.rejected}</h3>
                            <p>Rejected</p>
                        </div>
                    </div>
                </div>


                {/* Main management card */}
                <div
                    className={`speaker-content-layout ${selectedSpeaker ? "has-talk-panel" : ""
                        }`}
                    style={{
                        "--talk-panel-width": `${detailsPanelWidth}px`,
                    }}
                >
                    <section className="speaker-management-card">



                        <div className="speaker-management-topbar">

                            {/* Left: Event logo and name */}
                            <div className="speaker-event-info">
                                <img
                                    src={
                                        event?.banner_image?.base64 ||
                                        event?.banner_image?.url ||
                                        "/default-event-logo.png"
                                    }
                                    alt="Event logo"
                                    className="speaker-event-logo"
                                />

                                <div className="speaker-event-text">
                                    <h2 className="speaker-event-name">
                                        {event?.event_name || "Event Registrations"}
                                    </h2>

                                </div>
                            </div>

                            {/* Right: Buttons and search */}
                            <div className="speaker-management-toolbar">

                                <div className="speaker-management-buttons">
                                    <button
                                        type="button"
                                        className="speaker-btn speaker-btn-primary"
                                        onClick={() =>
                                            navigate(`/speaker-form/event/${eventId}`)
                                        }
                                    >
                                        <i className="fas fa-user-plus" />
                                        Add New Speaker
                                    </button>

                                    <button
                                        type="button"
                                        className="speaker-btn speaker-btn-outline"
                                        onClick={downloadSpeakerFormCSV}
                                    >
                                        <i className="fas fa-download" />
                                        Download Speakers CSV
                                    </button>
                                </div>

                                <div className="speaker-search-box">
                                    <i className="fas fa-search" />

                                    <input
                                        type="search"
                                        placeholder="Search speaker..."
                                        value={speakerSearchInput}
                                        onChange={(e) =>
                                            setSpeakerSearchInput(e.target.value)
                                        }
                                        aria-label="Search speakers"
                                    />

                                    {speakerSearchInput && (
                                        <button
                                            type="button"
                                            className="speaker-clear-search"
                                            aria-label="Clear search"
                                            onClick={() => setSpeakerSearchInput("")}
                                        >
                                            &times;
                                        </button>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* Speakers table */}
                        <div className="speaker-table-wrapper">
                            <table className="speaker-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>

                                        <th
                                            className="speaker-sortable"
                                            onClick={() => handleSort("email_id")}
                                        >
                                            <i className="fas fa-envelope" style={{ marginRight: "6px" }}></i> Email
                                            <span className="speaker-sort-icon">
                                                {speakerSortColumn === "email_id"
                                                    ? speakerSortOrder === "ASC" ? "↑" : "↓"
                                                    : "↕"}
                                            </span>
                                        </th>

                                        <th>
                                            <i className="fas fa-file-alt" style={{ marginRight: "6px" }}></i>
                                            Talk Topic
                                        </th>
                                        <th>  <i className="fas fa-file-alt" style={{ marginRight: "6px" }} />Talk Details</th>
                                        <th
                                            className="speaker-sortable"
                                            onClick={() => handleSort("created_at")}
                                        >
                                            Submitted On
                                            <span className="speaker-sort-icon">
                                                {speakerSortColumn === "created_at"
                                                    ? speakerSortOrder === "ASC" ? "↑" : "↓"
                                                    : "↕"}
                                            </span>
                                        </th>


                                        <th
                                            className="speaker-sortable"
                                            onClick={() => handleSort("status")}
                                        >
                                            <i className="fas fa-tag" style={{ marginRight: "6px" }}></i>
                                            Status
                                            <span className="speaker-sort-icon">
                                                {speakerSortColumn === "status"
                                                    ? speakerSortOrder === "ASC" ? "↑" : "↓"
                                                    : "↕"}
                                            </span>
                                        </th>



                                        <th>
                                            <i className="fas fa-user" /> Profile
                                        </th>

                                        <th>
                                            <i className="fas fa-cog" /> Actions
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {speakersLoading ? (
                                        <tr>
                                            <td colSpan={7} className="speaker-table-message">
                                                <i className="fas fa-spinner fa-spin" />
                                                <span>Loading speakers...</span>
                                            </td>
                                        </tr>
                                    ) : speakersData.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="speaker-table-message">
                                                <i className="fas fa-user-slash" />
                                                <span>
                                                    {speakerSearch
                                                        ? "No speakers match your search."
                                                        : "No speakers registered for this event yet."}
                                                </span>
                                            </td>
                                        </tr>
                                    ) : (
                                        speakersData.map((speaker, index) => {
                                            const registrationId =
                                                speaker.registration_id ?? speaker.id;

                                            const status = getStatus(speaker);
                                            const isSettled =
                                                status === "Approved" ||
                                                status === "Rejected";

                                            const isActionLoading =
                                                actionLoading === registrationId;

                                            return (
                                                <tr key={registrationId ?? index}>
                                                    <td className="speaker-name-cell">
                                                        {getSpeakerName(speaker)}
                                                    </td>

                                                    <td>{getSpeakerEmail(speaker)}</td>

                                                    <td className="speaker-topic-cell">
                                                        {getTalkTopic(speaker)}
                                                    </td>

                                                    <td className="speaker-talk-details-cell">
                                                        <button
                                                            type="button"
                                                            className="speaker-talk-details-btn"
                                                            onClick={() => openSpeakerTalkDetails(speaker)}
                                                            aria-label={`View talk details for ${getSpeakerName(speaker)}`}
                                                        >

                                                            <span>View Talk</span>

                                                        </button>
                                                    </td>

                                                    <td>
                                                        {formatDate(
                                                            speaker.submitted_at ||
                                                            speaker.created_at
                                                        )}
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`speaker-status speaker-status-${status.toLowerCase()}`}
                                                        >
                                                            {status}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="speaker-btn speaker-btn-view"
                                                            title="View speaker profile"
                                                            aria-label={`View profile of ${getSpeakerName(speaker)}`}
                                                            onClick={() =>
                                                                window.open(
                                                                    `/speaker-profile/${speaker.id}`,
                                                                    "_blank",
                                                                    "noopener,noreferrer"
                                                                )
                                                            }
                                                        >
                                                            <i className="fas fa-user" />
                                                            <span>View</span>
                                                        </button>
                                                    </td>

                                                    <td>
                                                        <div className="speaker-manage-actions">
                                                            <button
                                                                type="button"
                                                                className="speaker-icon-btn speaker-icon-approve"
                                                                title="Approve speaker"
                                                                aria-label="Approve speaker"
                                                                disabled={isSettled || isActionLoading}
                                                                onClick={() =>
                                                                    handleSpeakerAction(registrationId, "approve")
                                                                }
                                                            >
                                                                {isActionLoading ? (
                                                                    <i className="fas fa-spinner fa-spin" />
                                                                ) : (
                                                                    <i className="fas fa-check" />
                                                                )}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="speaker-icon-btn speaker-icon-reject"
                                                                title="Reject speaker"
                                                                aria-label="Reject speaker"
                                                                disabled={isSettled || isActionLoading}
                                                                onClick={() =>
                                                                    handleSpeakerAction(registrationId, "reject")
                                                                }
                                                            >
                                                                <i className="fas fa-times" />
                                                            </button>
                                                        </div>


                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="speaker-pagination">
                            <div className="speaker-pagination-info">
                                Showing {speakersData.length} speakers on page{" "}
                                {speakerPage}
                            </div>

                            <div className="speaker-pagination-controls">
                                <button
                                    className="speaker-btn speaker-btn-outline"
                                    disabled={speakerPage <= 1 || speakersLoading}
                                    onClick={() =>
                                        setSpeakerPage((page) => Math.max(1, page - 1))
                                    }
                                >
                                    <i className="fas fa-chevron-left" />
                                    Previous
                                </button>

                                <span className="speaker-page-count">
                                    Page <strong>{speakerPage}</strong> of{" "}
                                    <strong>{speakerTotalPages}</strong>
                                </span>

                                <button
                                    className="speaker-btn speaker-btn-outline"
                                    disabled={
                                        speakerPage >= speakerTotalPages ||
                                        speakersLoading
                                    }
                                    onClick={() => setSpeakerPage((page) => page + 1)}
                                >
                                    Next
                                    <i className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>

                    </section>



                    {selectedSpeaker && (
                        <aside className="speaker-talk-panel">
                            <div className="speaker-talk-panel-header">
                                <div>
                                    <h2>Speaker Form</h2>
                                    <p>{getSpeakerName(selectedSpeaker)}</p>
                                </div>

                                <button
                                    type="button"
                                    className="speaker-talk-panel-close"
                                    onClick={closeSpeakerTalkDetails}
                                    aria-label="Close talk details"
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="speaker-talk-panel-resize">
                                <label htmlFor="talk-panel-width">
                                    Panel width
                                </label>

                                <input
                                    id="talk-panel-width"
                                    type="range"
                                    min="320"
                                    max="720"
                                    step="20"
                                    value={detailsPanelWidth}
                                    onChange={(e) =>
                                        setDetailsPanelWidth(Number(e.target.value))
                                    }
                                />

                                {/* <span>{detailsPanelWidth}px</span> */}
                            </div>

                            <div className="speaker-talk-panel-body">
                                {talkDetailsLoading ? (
                                    <div className="speaker-talk-panel-message">
                                        <i className="fas fa-spinner fa-spin" />
                                        Loading talk details...
                                    </div>
                                ) : talkDetailsError ? (
                                    <div className="speaker-talk-panel-error">
                                        {talkDetailsError}
                                    </div>
                                ) : (
                                    <>

                                        {Object.entries(
                                            speakerTalkDetails?.data ??
                                            speakerTalkDetails?.result ??
                                            speakerTalkDetails ??
                                            {}
                                        )
                                            .filter(
                                                ([key]) =>
                                                    !["success", "message"].includes(
                                                        key.toLowerCase()
                                                    )
                                            )
                                            .map(([key, value]) => (
                                                <div
                                                    className="speaker-talk-field"
                                                    key={key}
                                                >
                                                    <span>
                                                        {key
                                                            .replace(/_/g, " ")
                                                            .replace(/\b\w/g, (c) =>
                                                                c.toUpperCase()
                                                            )}
                                                    </span>

                                                    <p>
                                                        {value == null || value === ""
                                                            ? "Not provided"
                                                            : typeof value === "object"
                                                                ? JSON.stringify(value, null, 2)
                                                                : String(value)}
                                                    </p>
                                                </div>
                                            ))}
                                    </>
                                )}
                            </div>
                        </aside>
                    )}
                </div>


            </div>
        </DashboardLayout>
    );
};

export default SpeakerManagementPage;
